import React, { useMemo } from 'react';
import {
  Box,
  SimpleGrid,
  Heading,
  Text,
  Card,
  CardBody,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  HStack,
  Badge,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Scatter,
  ZAxis,
} from 'recharts';
import { FaShieldAlt, FaChartPie, FaProjectDiagram, FaLightbulb } from 'react-icons/fa';
import { schemeSet3 } from 'd3-scale-chromatic';

const AnalyticsDashboard = ({ data }) => {
  const analytics = useMemo(() => {
    const categories = Object.entries(data.categories);
    const totalCompanies = categories.reduce(
      (acc, [_, cat]) => acc + cat.companies.length,
      0
    );

    // Category size analysis
    const categorySizes = categories.map(([key, cat]) => ({
      name: cat.name,
      value: cat.companies.length,
      percentage: (cat.companies.length / totalCompanies * 100).toFixed(1)
    })).sort((a, b) => b.value - a.value);

    // Company overlap analysis
    const companyFrequency = {};
    categories.forEach(([_, cat]) => {
      cat.companies.forEach(company => {
        companyFrequency[company] = (companyFrequency[company] || 0) + 1;
      });
    });

    const multiCategoryCompanies = Object.entries(companyFrequency)
      .filter(([_, count]) => count > 1)
      .map(([company, count]) => ({
        name: company,
        categories: count
      }))
      .sort((a, b) => b.categories - a.categories);

    // Market focus distribution
    const marketFocus = {
      enterprise: categories.filter(([_, cat]) => 
        cat.name.toLowerCase().includes('enterprise') ||
        cat.name.toLowerCase().includes('infrastructure')
      ).length,
      consumer: categories.filter(([_, cat]) => 
        cat.name.toLowerCase().includes('consumer') ||
        cat.name.toLowerCase().includes('identity')
      ).length,
      hybrid: categories.length - 
        (categories.filter(([_, cat]) => 
          cat.name.toLowerCase().includes('enterprise') ||
          cat.name.toLowerCase().includes('infrastructure') ||
          cat.name.toLowerCase().includes('consumer') ||
          cat.name.toLowerCase().includes('identity')
        ).length)
    };

    // Technology trends
    const techTrends = [
      {
        name: 'AI/ML Security',
        companies: categories.filter(([_, cat]) => 
          cat.name.toLowerCase().includes('ai') ||
          cat.name.toLowerCase().includes('machine learning')
        ).reduce((acc, [_, cat]) => acc + cat.companies.length, 0)
      },
      {
        name: 'Cloud Security',
        companies: categories.filter(([_, cat]) => 
          cat.name.toLowerCase().includes('cloud')
        ).reduce((acc, [_, cat]) => acc + cat.companies.length, 0)
      },
      {
        name: 'Zero Trust',
        companies: categories.filter(([_, cat]) => 
          cat.name.toLowerCase().includes('zero trust')
        ).reduce((acc, [_, cat]) => acc + cat.companies.length, 0)
      },
      {
        name: 'IoT Security',
        companies: categories.filter(([_, cat]) => 
          cat.name.toLowerCase().includes('iot')
        ).reduce((acc, [_, cat]) => acc + cat.companies.length, 0)
      }
    ].sort((a, b) => b.companies - a.companies);

    return {
      totalCompanies,
      totalCategories: categories.length,
      categorySizes,
      multiCategoryCompanies,
      marketFocus,
      techTrends,
      averageCompaniesPerCategory: (totalCompanies / categories.length).toFixed(1)
    };
  }, [data]);

  return (
    <Box p={4}>
      <VStack spacing={6} align="stretch">
        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Companies</StatLabel>
                <StatNumber>{analytics.totalCompanies}</StatNumber>
                <StatHelpText>Unique cybersecurity vendors</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Categories</StatLabel>
                <StatNumber>{analytics.totalCategories}</StatNumber>
                <StatHelpText>Distinct security domains</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Multi-Category Companies</StatLabel>
                <StatNumber>{analytics.multiCategoryCompanies.length}</StatNumber>
                <StatHelpText>Companies in multiple domains</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Avg Companies per Category</StatLabel>
                <StatNumber>{analytics.averageCompaniesPerCategory}</StatNumber>
                <StatHelpText>Category density</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Category Distribution */}
        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <FaChartPie />
                <Heading size="md">Category Distribution</Heading>
              </HStack>
              <Box height="400px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.categorySizes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      fontSize={12}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#4299E1">
                      {analytics.categorySizes.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={schemeSet3[index % schemeSet3.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Market Focus Distribution */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack>
                  <FaProjectDiagram />
                  <Heading size="md">Market Focus Distribution</Heading>
                </HStack>
                <Box height="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Object.entries(analytics.marketFocus).map(([key, value]) => ({
                          name: key.charAt(0).toUpperCase() + key.slice(1),
                          value
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {Object.entries(analytics.marketFocus).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={schemeSet3[index]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Technology Trends */}
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack>
                  <FaLightbulb />
                  <Heading size="md">Technology Trends</Heading>
                </HStack>
                <Stack spacing={4}>
                  {analytics.techTrends.map((trend, index) => (
                    <Box key={index}>
                      <HStack justify="space-between" mb={2}>
                        <Text>{trend.name}</Text>
                        <Badge colorScheme="blue">{trend.companies} companies</Badge>
                      </HStack>
                      <Progress 
                        value={trend.companies} 
                        max={analytics.totalCompanies}
                        colorScheme="blue"
                        hasStripe
                      />
                    </Box>
                  ))}
                </Stack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Multi-Category Companies */}
        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <FaShieldAlt />
                <Heading size="md">Multi-Category Leaders</Heading>
              </HStack>
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Company</Th>
                      <Th isNumeric>Categories</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {analytics.multiCategoryCompanies.slice(0, 10).map((company, index) => (
                      <Tr key={index}>
                        <Td>{company.name}</Td>
                        <Td isNumeric>
                          <Badge colorScheme="blue">{company.categories}</Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default AnalyticsDashboard;